import { columnIds, useSearch } from './use-search'
import Breadcrumb from 'lib/components/breadcrumb/breadcrumb'
import { AnimatedLoadingScreen, EmptySearchScreen } from '../requests/empty-screens'
import Filters from './filters'
import Button, { LinkLikeButton } from 'components/core/button'
import { FolderOpenIcon, LoaderCircleIcon } from 'lucide-react'
import { useFirstRender } from './use-first-render'
import ConfirmationModal from 'components/elements/confirmation-modal'
import { useConfirmationModalContext } from 'providers/confirmation-modal-provider'
import SearchRequestsTable from './search-requests-table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'lib/components/tabs'
import { useFeatureFlagsContext } from 'lib/components/feature-flags/feature-flags-provider'
import { updateParams } from 'lib/util/url'
import ProjectList from 'components/elements/projects/project-list'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import Badge from 'lib/components/badge/badge'
import { Project } from 'interfaces/project'
import { ProjectUpdateType } from 'components/elements/projects/project-drawer'
import { SearchV2Response } from 'interfaces/ticket'

export const getResultsTitle = (search: string, count: number) => {
  if (search) {
    return `Results for "${search.slice(0, 50)}${search.length > 50 ? '...' : ''}" (${count})`
  }
  return `Results (${count})`
}

export const Search = () => {
  const useSearchRef = useSearch()
  const { query, count, loading, error, table, projectsQuery, projectsQueryKey } = useSearchRef
  const { isModalVisible, modalData, setModalVisible } = useConfirmationModalContext()
  const { loading: loadingFirstRender } = useFirstRender()
  const queryClient = useQueryClient()
  const { isFeatureFlagEnabled } = useFeatureFlagsContext()
  const params = new URLSearchParams(window.location.search)
  const [currentTab, setCurrentTab] = useState(params.get('tab') || 'all')

  const emptyScreen = query.data?.data?.length === 0 && <EmptySearchScreen />
  const errorScreen = error && (
    <div className="tw-mt-4 tw-flex tw-max-w-prose tw-flex-col tw-items-center tw-justify-center">
      <AnimatedLoadingScreen
        variant="warning"
        text="Something went wrong."
        subText={
          <span>
            Try refreshing the page, checking back later, or contacting support at{' '}
            <a href="mailto:help@designpickle.com">help@designpickle.com</a>.
          </span>
        }
      />
      <Button
        onClick={() => {
          window.location.reload()
        }}
        color="neutral"
        className="tw-mt-4"
      >
        Try again
      </Button>
    </div>
  )

  const setTab = (tab) => {
    if (['requests', 'projects', 'all'].includes(tab)) {
      const newParams = new URLSearchParams(window.location.search)
      newParams.set('tab', tab)
      updateParams(newParams)
      setCurrentTab(tab)
    }
  }

  const updateProjectsResults = (project: Project, action: ProjectUpdateType) => {
    queryClient.setQueryData(projectsQueryKey, (old: SearchV2Response<Project> | undefined) => {
      if (!old) return old

      const index = old.hits.hits.findIndex((hit) => hit._source.id === project.id)
      if (action === ProjectUpdateType.DELETE) {
        if (index !== -1) {
          old.hits.hits.splice(index, 1)
        }
        return {
          ...old,
          hits: {
            ...old.hits,
            total: {
              ...old.hits.total,
              value: old.hits.total.value - 1,
            },
          },
        }
      }

      if (action === ProjectUpdateType.UPDATE) {
        if (index !== -1) {
          const newResult = { ...old }
          newResult.hits.hits[index]._source = project
          return newResult
        }
      }

      return old
    })
  }

  const ProjectsSection = ({ short }: { short?: boolean }) => (
    <>
      <div className="tw-flex tw-items-center tw-justify-between tw-py-4">
        <h4 className="tw-m-0 tw-flex tw-items-center tw-gap-1 tw-p-0">
          <FolderOpenIcon />
          Projects ({projectsQuery.data?.data?.length})
        </h4>
        {currentTab !== 'projects' && <LinkLikeButton onClick={() => setTab('projects')}>See all</LinkLikeButton>}
      </div>
      {projectsQuery.data?.data?.length !== 0 && (
        <ProjectList
          projects={short ? projectsQuery.data.data.slice(0, 5) : projectsQuery.data.data}
          onUpdate={updateProjectsResults}
        />
      )}
    </>
  )

  const TicketsHeader = () => (
    <div className="tw-flex tw-items-center tw-justify-between tw-py-4">
      <h4 className="tw-m-0 tw-flex tw-items-center tw-gap-1 tw-p-0">
        <FolderOpenIcon />
        Requests ({count})
      </h4>
    </div>
  )

  if (loading || loadingFirstRender) {
    return (
      <div className="tw-flex tw-h-[60vh] tw-items-center tw-justify-center">
        <AnimatedLoadingScreen
          variant="spinner"
          text="We're searching your content..."
          subText="Hang tight while we locate what you're looking for."
        />
      </div>
    )
  }

  return (
    <>
      <ConfirmationModal
        title={modalData['title']}
        message={modalData['message']}
        confirmBtnText={modalData['confirmBtnText']}
        confirmAction={modalData['confirmAction']}
        cancelAction={() => setModalVisible(false)}
        visible={isModalVisible}
      />
      <Breadcrumb className="tw-mt-3" text="Back to requests" href={`/requests`} />
      <Tabs value={currentTab} onChange={setTab}>
        <div className="tw-flex tw-flex-col">
          <div className="tw-flex tw-flex-row tw-items-center tw-justify-between">
            <h1 className="tw-m-0 tw-mt-2 tw-p-0 tw-text-2xl">
              {getResultsTitle(
                Array.isArray(table.getColumn(columnIds.query).getFilterValue())
                  ? table.getColumn(columnIds.query).getFilterValue()[0]
                  : table.getColumn(columnIds.query).getFilterValue(),
                count,
              )}
            </h1>
            {/* TODO: Add saved searches back in once we fully launch Elasticsearch */}
            {/* <SavedSearches setFilters={table.setColumnFilters} /> */}
          </div>
          <div>
            {isFeatureFlagEnabled('search_v2') && (
              <TabsList className="tw-my-2">
                <TabsTrigger value="all" onClick={() => setTab('all')}>
                  All&nbsp;
                  <Badge color="white" fontWeight="normal" style="rounded">
                    {projectsQuery.isFetching || loading ? (
                      <LoaderCircleIcon className="lu-sm tw-animate-spin" />
                    ) : (
                      projectsQuery.data?.data?.length + count
                    )}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="projects" onClick={() => setTab('projects')}>
                  Projects&nbsp;
                  <Badge color="white" fontWeight="normal" style="rounded">
                    {projectsQuery.isFetching ? (
                      <LoaderCircleIcon className="lu-sm tw-animate-spin" />
                    ) : (
                      projectsQuery.data?.data?.length
                    )}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="requests" onClick={() => setTab('requests')}>
                  Requests&nbsp;
                  <Badge color="white" fontWeight="normal" style="rounded">
                    {loading ? <LoaderCircleIcon className="lu-sm tw-animate-spin" /> : count}
                  </Badge>
                </TabsTrigger>
              </TabsList>
            )}
          </div>
          <div className="tw-flex tw-items-center tw-py-2">
            <Filters table={table} />
          </div>
        </div>
        {errorScreen || emptyScreen ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center"> {errorScreen || emptyScreen}</div>
        ) : (
          <>
            <TabsContent value="all">
              {isFeatureFlagEnabled('search_v2') && <ProjectsSection short />}
              <TicketsHeader />
              <SearchRequestsTable useSearch={useSearchRef} />
            </TabsContent>
            <TabsContent value="projects">
              <ProjectsSection />
            </TabsContent>
            <TabsContent value="requests">
              <TicketsHeader />
              <SearchRequestsTable useSearch={useSearchRef} />
            </TabsContent>
          </>
        )}
      </Tabs>
    </>
  )
}
